/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { setTitle } from "@lemmonhq/orders";
import {
  SectionedBody,
  type SectionedBodySection,
  useLocalization,
} from "@lemmonhq/uikit";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";
import { useToast } from "@app/vue/providers/Toast";
import { useTrpc } from "@app/vue/providers/TrpcProvider";

export const GroupBaseSettingsPage = defineComponent({
  name: "GroupBaseSettingsPage",
  setup() {
    const { t } = useLocalization();
    const auth = useGroupAuth();
    const trpc = useTrpc();
    const toast = useToast();

    const canEdit = auth.isGroupAdmin;
    const saving = ref(false);
    const saveSuccess = ref(false);
    const name = ref(auth.group.value.name);

    // Keep the field in sync if the session group changes underneath us.
    watch(
      () => auth.group.value.name,
      (next) => {
        if (!saving.value) name.value = next;
      },
    );

    onMounted(() => setTitle(t("admin.settings.title")));

    const sections = computed<SectionedBodySection[]>(() => [
      {
        id: "group-information",
        label: t("admin.settings.group_information"),
        icon: "domain",
      },
    ]);

    const trimmedName = computed(() => name.value.trim());
    const hasChanges = computed(
      () =>
        trimmedName.value.length > 0 &&
        trimmedName.value !== auth.group.value.name,
    );

    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      if (!hasChanges.value || saving.value || !canEdit.value) return;

      saving.value = true;
      saveSuccess.value = false;
      try {
        const updated = await trpc.groups.update.mutate({
          name: trimmedName.value,
        });
        // Reflect the new name across the session (navbar, headers, etc.).
        auth.session.value = {
          ...auth.session.value,
          group: { ...auth.session.value.group, name: updated.name },
        };
        name.value = updated.name;
        saveSuccess.value = true;
        toast.showToast(t("admin.settings.group_saved"), { type: "success" });
        setTimeout(() => {
          saveSuccess.value = false;
        }, 3000);
      } catch (error) {
        console.error("Failed to update group:", error);
        toast.showToast(t("admin.settings.group_save_error"), {
          type: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="settings"
          title={t("admin.settings.title")}
          description={t("admin.settings.desc")}
        >
          {{
            actions: () =>
              canEdit.value ? (
                <div class="flex items-center gap-3">
                  {saveSuccess.value && (
                    <div class="flex items-center gap-2 text-sm font-medium text-success">
                      <span class="material-symbols-outlined text-lg">
                        check_circle
                      </span>
                      {t("admin.settings.group_saved")}
                    </div>
                  )}
                  <button
                    type="submit"
                    form="group-settings-form"
                    disabled={!hasChanges.value || saving.value}
                    class="btn btn-primary btn-sm gap-2 shadow-lg"
                  >
                    {saving.value ? (
                      <>
                        <span class="loading loading-spinner loading-sm"></span>
                        {t("admin.settings.saving")}
                      </>
                    ) : (
                      <>
                        <span class="material-symbols-outlined text-lg">
                          save
                        </span>
                        {t("admin.settings.save_changes")}
                      </>
                    )}
                  </button>
                </div>
              ) : null,
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent>
            <form
              id="group-settings-form"
              onSubmit={handleSubmit}
              class="mx-auto"
            >
              <SectionedBody
                sections={sections.value}
                ariaLabel={t("admin.settings.title")}
              >
                {{
                  "group-information": () => (
                    <div class="l-box-shadow rounded-xl border border-base-300/50 bg-base-100 p-6 shadow-sm">
                      <div class="mb-6 flex items-center gap-3">
                        <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <span class="material-symbols-outlined text-xl text-primary">
                            domain
                          </span>
                        </div>
                        <div>
                          <h3 class="text-lg font-semibold text-accent">
                            {t("admin.settings.group_information")}
                          </h3>
                          <p class="text-sm text-base-content/60">
                            {t("admin.settings.group_information_desc")}
                          </p>
                        </div>
                      </div>

                      <div class="grid grid-cols-1 gap-4">
                        <div>
                          <label class="mb-2 block text-sm font-medium text-base-content/80">
                            {t("admin.settings.group_name")} *
                          </label>
                          <input
                            type="text"
                            value={name.value}
                            disabled={!canEdit.value}
                            onInput={(e) => {
                              name.value = (e.target as HTMLInputElement).value;
                              saveSuccess.value = false;
                            }}
                            class="input input-bordered w-full bg-base-200 transition-colors focus:bg-base-100"
                            placeholder={t("admin.settings.group_name")}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ),
                }}
              </SectionedBody>
            </form>
          </AppSubpageContent>
        </AppSubpage>
      </div>
    );
  },
});
